import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, RouterProvider, Outlet, Navigate, useLocation, useRouteError } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { useAnalyticsPageViews } from './hooks/useAnalyticsPageViews'
import { useAnalyticsUserProperties } from './hooks/useAnalyticsUserProperties'
import { useLastActiveStamp } from './hooks/useLastActiveStamp'
import { useLocaleMirror } from './hooks/useLocaleMirror'
import { useReadMeter } from './hooks/useReadMeter'
import { useDeepLinkHandler } from './hooks/useDeepLinkHandler'
import { useOTACheck } from './hooks/useOTACheck'
import { useCanonicalLink, useNoIndex } from './hooks/useSeoTags'
import { usePerfRouteTracking } from './hooks/usePerfRouteTracking'
import { markNavigationStart } from './lib/perfNavigation'
import { connectHistoryUiStore } from './lib/historyUiStore'
import { usePushNotifications } from './hooks/usePushNotifications'
import { usePendingTotemFollow } from './hooks/usePendingTotemFollow'
import { usePendingSetPicks } from './hooks/usePendingSetPicks'
import { useBlockBackAtRoot } from './hooks/useBlockBackAtRoot'
import { useRouteScrollRestore } from './hooks/useRouteScrollRestore'
import { useOverlayStallGuard } from './hooks/useOverlayStallGuard'
import { useTrackPreviousPath } from './lib/viewTransition'
import { useScheduleFilterSessionReset } from './lib/scheduleFilterSession'
import { GuestGuardProvider } from './hooks/useGuestGuard'
import { useWarmSearchIndex } from './hooks/useWarmSearchIndex'
import { LinkGroupAfterRsvp } from './components/LinkGroupAfterRsvp'
import { InviteAttachment } from './components/InviteAttachment'
import { Sentry } from './lib/sentry'
import { log } from './lib/logger'
import { ErrorFallback } from './components/ErrorFallback'
import { NotFoundState } from './components/NotFoundState'
import LoginPage from './pages/LoginPage'
import HomePage from './pages/HomePage'
import EventPage from './pages/EventPage'
import EventBrowse from './pages/EventBrowse'
import SearchPage from './pages/SearchPage'
import SocialPage from './pages/SocialPage'
import GroupPage from './pages/GroupPage'
import JoinPage from './pages/JoinPage'
import FollowTotemPage from './pages/FollowTotemPage'
import ProfilePage from './pages/ProfilePage'
import AddFriendPage from './pages/AddFriendPage'
import UserProfilePage from './pages/UserProfilePage'
import ArtistPage from './pages/ArtistPage'
import SettingsPage from './pages/SettingsPage'
import CalendarSyncPage from './pages/CalendarSyncPage'
import NotificationSettingsPage from './pages/NotificationSettingsPage'
import MyRequestsPage from './pages/MyRequestsPage'
import SupportPage from './pages/SupportPage'
import BeaconRenamePage from './pages/BeaconRenamePage'
import { AdminRoute } from './components/AdminRoute'
import { BottomChrome } from './components/BottomChrome'
import { PregameRsvpBanner } from './components/PregameRsvpBanner'
import { BottomNav } from './components/BottomNav'
import { TabKeepAlive } from './components/TabKeepAlive'
import { activeTabPath, type KeptTab } from './lib/keptTabs'
import { PreviewLoadPrompt } from './components/PreviewLoadPrompt'
import { reloadForStaleChunk } from './lib/staleChunkReload'
import DevSignInPage from './pages/DevSignInPage'

const EventSchedulePage = lazy(() => import('./pages/EventPage/EventSchedulePage'))
const BlockedUsersPage = lazy(() => import('./pages/BlockedUsersPage'))
const FriendsPage = lazy(() => import('./pages/FriendsPage'))
const SettingsDebugPage = lazy(() => import('./pages/SettingsDebugPage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const AgentAccessPage = lazy(() => import('./pages/AgentAccessPage'))
const OAuthConsentPage = lazy(() => import('./pages/OAuthConsentPage'))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'))
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'))
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'))
const AdminArtistsPage = lazy(() => import('./pages/AdminArtistsPage'))
const AdminEventsPage = lazy(() => import('./pages/AdminEventsPage'))
const AdminEventEditorPage = lazy(() => import('./pages/AdminEventEditorPage'))
const AdminReviewPage = lazy(() => import('./pages/AdminReviewPage'))
const AdminSuggestionsPage = lazy(() => import('./pages/AdminSuggestionsPage'))
const AdminRegionRequestsPage = lazy(() => import('./pages/AdminRegionRequestsPage'))
const AdminScheduleLeadsPage = lazy(() => import('./pages/AdminScheduleLeadsPage'))
const AdminSetReportsPage = lazy(() => import('./pages/AdminSetReportsPage'))
const AdminEventReportsPage = lazy(() => import('./pages/AdminEventReportsPage'))
const AdminWatchlistPage = lazy(() => import('./pages/AdminWatchlistPage'))
const AdminProviderResearchPage = lazy(() => import('./pages/AdminProviderResearchPage'))
const AdminDiscoveryMetricsPage = lazy(() => import('./pages/AdminDiscoveryMetricsPage'))
const AdminRoutineRunsPage = lazy(() => import('./pages/AdminRoutineRunsPage'))
const AdminRoutineMetricsPage = lazy(() => import('./pages/AdminRoutineMetricsPage'))
const AdminFeedbackPage = lazy(() => import('./pages/AdminFeedbackPage'))
const AdminMetricsPage = lazy(() => import('./pages/AdminMetricsPage'))
const AdminEventMetricsPage = lazy(() => import('./pages/AdminEventMetricsPage'))
const FestivalModePage = lazy(() => import('./pages/FestivalModePage'))
const FestivalNowRoute = lazy(() => import('./pages/FestivalModePage/NowRoute'))
const FestivalScheduleRoute = lazy(() => import('./pages/FestivalModePage/ScheduleRoute'))
const FestivalFriendsRoute = lazy(() => import('./pages/FestivalModePage/FriendsRoute'))
const FestivalFriendDetailRoute = lazy(() => import('./pages/FestivalModePage/FriendDetailRoute'))
const FestivalMapRoute = lazy(() => import('./pages/FestivalModePage/MapRoute'))
const FestivalTotemRoute = lazy(() => import('./pages/FestivalModePage/TotemRoute'))
const FestivalPickerPage = lazy(() => import('./pages/FestivalPickerPage'))
const AdminClockPage = lazy(() => import('./pages/AdminClockPage'))
const TotemManagePage = lazy(() => import('./pages/TotemManagePage'))
const GroupSettingsPage = lazy(() => import('./pages/GroupSettingsPage'))

function AppLoader() {
  return (
    <div className="min-h-dvh bg-background flex items-center justify-center">
      <div className="pointer-events-none fixed inset-0 flex items-center justify-center">
        <div className="h-[600px] w-[600px] rounded-full bg-primary/15 blur-[120px]" />
      </div>
      <span className="relative z-10 text-2xl font-bold tracking-tight text-foreground/30 animate-pulse">
        Faro
      </span>
    </div>
  )
}

function RouteErrorBoundary() {
  const error = useRouteError()
  useEffect(() => {
    if (reloadForStaleChunk(error)) return
    log('router', 'route error caught', {
      message: error instanceof Error ? error.message : String(error),
      url: window.location.href,
      pathname: window.location.pathname,
    })
    Sentry.captureException(error)
  }, [error])
  return <ErrorFallback resetError={() => { window.location.href = '/' }} />
}

function NotFoundPage() {
  useNoIndex()
  useEffect(() => {
    log('router', '404 — no route matched', {
      url: window.location.href,
      pathname: window.location.pathname,
    })
  }, [])
  return (
    <NotFoundState
      message={<fbt desc="Message shown when user navigates to a URL that does not exist">Page not found</fbt>}
    />
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <AppLoader />
  return user ? <>{children}</> : <Navigate to="/login" replace state={{ from: location }} />
}

function LoginRoute() {
  const { signedInUid } = useAuth()
  const location = useLocation()
  if (signedInUid) {
    const from = (location.state as { from?: Location } | null)?.from
    const target = from ? `${from.pathname}${from.search}${from.hash}` : '/'
    return <Navigate to={target} replace />
  }
  return <LoginPage />
}

// The nav tabs' roots. They render through TabKeepAlive in RootLayout, which
// keeps them mounted between visits; their routes below only claim the path.
const TAB_PAGES: readonly KeptTab[] = [
  { path: '/', element: <HomePage /> },
  { path: '/discover', element: <EventBrowse /> },
  { path: '/search', element: <SearchPage /> },
  { path: '/social', element: <SocialPage /> },
  { path: '/profile', element: <ProfilePage /> },
]

function RootLayout() {
  const { user } = useAuth()
  const { pathname } = useLocation()
  const onTab = activeTabPath(TAB_PAGES, pathname) !== undefined
  // Festival mode renders its own BottomChrome around FestivalBottomNav, so the
  // app-wide one steps aside rather than stacking a second bar at bottom-0.
  const festivalMode = pathname.startsWith('/live-festival/')
  useAnalyticsPageViews()
  useAnalyticsUserProperties()
  useLastActiveStamp()
  useLocaleMirror()
  useReadMeter()
  useDeepLinkHandler()
  useOTACheck()
  usePushNotifications(user)
  usePendingTotemFollow()
  usePendingSetPicks()
  useBlockBackAtRoot()
  useRouteScrollRestore()
  useTrackPreviousPath()
  useOverlayStallGuard()
  useScheduleFilterSessionReset()
  useWarmSearchIndex()
  useCanonicalLink()
  usePerfRouteTracking(TAB_PAGES)
  return (
    <GuestGuardProvider>
      <PreviewLoadPrompt />
      <LinkGroupAfterRsvp />
      <InviteAttachment />
      <div className="min-h-dvh flex flex-col bg-background">
        <div className="flex-1 min-h-0 pb-[calc(var(--nav-content-pad)+env(safe-area-inset-bottom))] md:pb-0">
          <Suspense fallback={<AppLoader />}>
            <TabKeepAlive tabs={TAB_PAGES} />
            {!onTab && <Outlet />}
          </Suspense>
        </div>
        {!festivalMode && (
          <BottomChrome>
            {user && !user.isAnonymous && <PregameRsvpBanner />}
            <BottomNav />
          </BottomChrome>
        )}
      </div>
    </GuestGuardProvider>
  )
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/login', element: <LoginRoute /> },
      ...TAB_PAGES.map(({ path }) => ({ path })),
      { path: '/events/:eventId', element: <EventPage /> },
      { path: '/events/:eventId/schedule', element: <EventSchedulePage /> },
      // Old tab routes stay as redirects: universal links and push deep links point at them.
      { path: '/festivals', element: <Navigate to="/discover?type=festival" replace /> },
      { path: '/parties', element: <Navigate to="/discover?type=party" replace /> },
      { path: '/festivals/all', element: <Navigate to="/discover?type=festival" replace /> },
      { path: '/parties/all', element: <Navigate to="/discover?type=party" replace /> },
      { path: '/groups', element: <Navigate to="/social" replace /> },
      {
        path: '/groups/:groupId',
        element: <ProtectedRoute><GroupPage /></ProtectedRoute>,
      },
      {
        path: '/groups/:groupId/settings',
        element: <ProtectedRoute><GroupSettingsPage /></ProtectedRoute>,
      },
      {
        path: '/groups/:groupId/totems',
        element: <ProtectedRoute><TotemManagePage /></ProtectedRoute>,
      },
      { path: '/join/:code', element: <JoinPage /> },
      { path: '/t/:groupId/:totemId', element: <FollowTotemPage /> },
      { path: '/privacy', element: <PrivacyPolicyPage /> },
      { path: '/terms', element: <TermsOfServicePage /> },
      { path: '/delete-account', element: <DeleteAccountPage /> },
      { path: '/friends', element: <FriendsPage /> },
      { path: '/add/:friendCode', element: <AddFriendPage /> },
      { path: '/users/:userId', element: <UserProfilePage /> },
      { path: '/artists/:artistId', element: <ArtistPage /> },
      ...(import.meta.env.DEV
        ? [{ path: '/dev-signin', element: <DevSignInPage /> }]
        : []),
      { path: '/settings', element: <SettingsPage /> },
      { path: '/settings/blocked', element: <BlockedUsersPage /> },
      { path: '/settings/agents', element: <ProtectedRoute><AgentAccessPage /></ProtectedRoute> },
      { path: '/settings/calendar', element: <CalendarSyncPage /> },
      { path: '/settings/notifications', element: <NotificationSettingsPage /> },
      { path: '/settings/debug', element: <SettingsDebugPage /> },
      { path: '/oauth/authorize', element: <OAuthConsentPage /> },
      { path: '/calendar-sync', element: <Navigate to="/settings/calendar" replace /> },
      { path: '/notification-settings', element: <Navigate to="/settings/notifications" replace /> },
      { path: '/requests', element: <MyRequestsPage /> },
      { path: '/support', element: <SupportPage /> },
      { path: '/beacon', element: <BeaconRenamePage /> },
      {
        path: '/admin',
        element: <AdminPage />,
      },
      {
        path: '/admin/artists',
        element: <AdminRoute><AdminArtistsPage /></AdminRoute>,
      },
      {
        path: '/admin/events',
        element: <AdminRoute><AdminEventsPage /></AdminRoute>,
      },
      {
        path: '/admin/pending',
        element: <AdminRoute><AdminReviewPage /></AdminRoute>,
      },
      {
        path: '/admin/feedback',
        element: <AdminRoute><AdminFeedbackPage /></AdminRoute>,
      },
      {
        path: '/admin/suggestions',
        element: <AdminRoute><AdminSuggestionsPage /></AdminRoute>,
      },
      {
        path: '/admin/regions',
        element: <AdminRoute><AdminRegionRequestsPage /></AdminRoute>,
      },
      {
        path: '/admin/schedule-leads',
        element: <AdminRoute><AdminScheduleLeadsPage /></AdminRoute>,
      },
      {
        path: '/admin/set-reports',
        element: <AdminRoute><AdminSetReportsPage /></AdminRoute>,
      },
      {
        path: '/admin/event-reports',
        element: <AdminRoute><AdminEventReportsPage /></AdminRoute>,
      },
      {
        path: '/admin/watchlist',
        element: <AdminRoute><AdminWatchlistPage /></AdminRoute>,
      },
      {
        path: '/admin/watchlist/providers',
        element: <AdminRoute><AdminProviderResearchPage /></AdminRoute>,
      },
      {
        path: '/admin/watchlist/metrics',
        element: <AdminRoute><AdminDiscoveryMetricsPage /></AdminRoute>,
      },
      {
        path: '/admin/routines',
        element: <AdminRoute><AdminRoutineRunsPage /></AdminRoute>,
      },
      {
        path: '/admin/routines/metrics',
        element: <AdminRoute><AdminRoutineMetricsPage /></AdminRoute>,
      },
      {
        path: '/admin/events/review',
        element: <Navigate to="/admin/pending" replace />,
      },
      {
        path: '/admin/events/:eventId',
        element: <AdminRoute><AdminEventEditorPage /></AdminRoute>,
      },
      {
        path: '/admin/events/:eventId/metrics',
        element: <AdminRoute><AdminEventMetricsPage /></AdminRoute>,
      },
      {
        path: '/admin/metrics',
        element: <AdminRoute><AdminMetricsPage /></AdminRoute>,
      },
      {
        path: '/admin/clock',
        element: <AdminRoute><AdminClockPage /></AdminRoute>,
      },
      {
        path: '/live-festival',
        element: <AdminRoute><FestivalPickerPage /></AdminRoute>,
      },
      {
        path: '/live-festival/:eventId',
        element: <FestivalModePage />,
        children: [
          { index: true, element: <FestivalNowRoute /> },
          { path: 'schedule', element: <FestivalScheduleRoute /> },
          { path: 'friends', element: <FestivalFriendsRoute /> },
          { path: 'friends/:uid', element: <FestivalFriendDetailRoute /> },
          { path: 'map', element: <FestivalMapRoute /> },
          { path: 'totem/:totemId', element: <FestivalTotemRoute /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

router.subscribe(state => markNavigationStart(state.location.pathname))
connectHistoryUiStore(router)

export default function App() {
  return <RouterProvider router={router} />
}
